import { useEffect, useRef, useState } from 'react';
import { asCaptureStatus, confirmSolAction } from '../../data/sol/sol-confirm-client';
import type { SolActionProposal } from '../../data/sol/sol-contract';
import { CAPTURE_STATUS_LABEL, formatCaptureNumber } from '../captures/capture-status';
import { useSolSession } from './sol-session';
import {
  ACTION_CANCELLED_TEXT,
  ACTION_CONFIRM_PROMPT,
  ACTION_EXPIRED_TEXT,
  ACTION_FAILED_TEXT,
  ACTION_SIGNED_OUT_TEXT,
  ACTION_STALE_TEXT,
  actionConfirmedText,
} from './sol-wording';

/**
 * THE CONFIRMATION CARD — M11 / S11-01.
 *
 * **NOTHING HAS HAPPENED WHEN THIS APPEARS.** Sol interpreted a request and
 * Crewlink bound it to one Capture the retrieval actually found; the card is
 * the whole of what was done. The write happens on Confirm, on a separate
 * endpoint, or it never happens at all.
 *
 * **IT SAYS WHAT IS CHANGING, IN FULL, BEFORE IT ASKS.** Capture ID, location,
 * Project, and the two statuses side by side — because a confirmation Brandon
 * cannot check is a confirmation he learns to tap without reading. The layout
 * is the design export's confirmation card (`m.isConfirm`): mono Capture ID,
 * bold location, the two status pills with an arrow, Confirm then Cancel.
 *
 * **CANCEL IS THE SAFE, CHEAP DEFAULT.** It sends nothing, writes nothing and
 * needs no server: the proposal simply expires unused. That is why it is a
 * plain button and not a second confirmation.
 *
 * **ONE TAP, ONCE.** Confirm is single-flight and becomes INERT on success —
 * not disabled-looking-tappable, but gone, replaced by what happened. Repeat
 * taps are also harmless at the writer (it is idempotent and the second call
 * writes no event), but "harmless underneath" is not a reason to allow a
 * control that looks like it is doing something.
 *
 * **THE DISPLAY IS NOT THE AUTHORITY.** Confirm sends the ticket and nothing
 * else. Editing this card in a browser changes what is on screen and nothing
 * about what is written.
 */

export interface SolActionProposalCardProps {
  readonly proposal: SolActionProposal;
  /**
   * Reload whatever the screen shows about this Capture, if anything. Optional
   * because Sol's own surface holds no Capture state to refresh — the Work Log
   * and the Capture screen read server truth when they are opened.
   */
  readonly onConfirmed?: ((captureId: string) => void) | undefined;
  /** Disabled while another Sol interaction owns the screen. */
  readonly disabled?: boolean | undefined;
  /** Injected in tests. Production uses the real clock. */
  readonly nowSeconds?: (() => number) | undefined;
}

type CardState =
  | { readonly kind: 'offered' }
  | { readonly kind: 'confirming' }
  | { readonly kind: 'done'; readonly text: string }
  | { readonly kind: 'stopped'; readonly text: string };

export function SolActionProposalCard({
  proposal,
  onConfirmed,
  disabled,
  nowSeconds,
}: SolActionProposalCardProps) {
  const session = useSolSession();
  /**
   * **SEEDED FROM THE VISIT, NOT FROM ZERO.** A card whose proposal was already
   * confirmed, cancelled or refused comes back settled — because navigating to
   * a source Capture and pressing Back used to remount this component at
   * "offered" and re-offer a change that had already been written.
   */
  const [state, setState] = useState<CardState>(
    () => session.settledActions.get(proposal.ticket) ?? { kind: 'offered' },
  );
  const mounted = useRef(true);

  /**
   * Record a TERMINAL outcome in the visit as well as on screen, so it survives
   * navigation. `offered` and `confirming` are not terminal and are never
   * stored — a card that was mid-request when the screen closed comes back
   * offered, and the server is still the thing that decides what a second
   * Confirm does.
   */
  const settle = (next: CardState): void => {
    if (next.kind === 'done' || next.kind === 'stopped') {
      session.settledActions.set(proposal.ticket, next);
    }
    setState(next);
  };
  /**
   * **THE SINGLE-FLIGHT LATCH, AND IT IS A REF FOR A REASON.** `state` is a
   * snapshot taken at render: two taps in one tick both read `offered`, so a
   * check against it would let both through and only the disabled attribute
   * would be standing between Brandon and two requests. A ref is written
   * immediately, before any await — which is the half that still holds when a
   * slow phone re-renders late.
   */
  const inFlight = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const { display } = proposal;
  const captureLabel = formatCaptureNumber(display.captureNumber);
  const fromLabel = CAPTURE_STATUS_LABEL[display.fromStatus];
  const toLabel = CAPTURE_STATUS_LABEL[display.toStatus];

  // **AN EXPIRED PROPOSAL IS NOT OFFERED.** Derived during render rather than
  // held in state: a card that sat on screen past its lifetime would present a
  // button whose only possible outcome is a refusal.
  const now = (nowSeconds ?? (() => Math.floor(Date.now() / 1000)))();
  const expired = state.kind === 'offered' && now >= proposal.expiresAt;

  const confirm = (): void => {
    // **SINGLE FLIGHT.** The guard runs before any await, so two taps in one
    // tick cannot both start a request.
    if (inFlight.current || state.kind !== 'offered' || disabled === true || expired) return;
    inFlight.current = true;
    setState({ kind: 'confirming' });

    void confirmSolAction({ ticket: proposal.ticket }).then((outcome) => {
      inFlight.current = false;
      if (!mounted.current) return;
      switch (outcome.status) {
        case 'confirmed': {
          const status = asCaptureStatus(outcome.captureStatus);
          settle({
            kind: 'done',
            // **THE STATUS THE SERVER REPORTED**, not the one that was proposed.
            text: actionConfirmedText(
              captureLabel,
              status === null ? outcome.captureStatus : CAPTURE_STATUS_LABEL[status],
            ),
          });
          onConfirmed?.(outcome.captureId);
          return;
        }
        case 'capture_changed':
          settle({ kind: 'stopped', text: ACTION_STALE_TEXT });
          return;
        case 'expired':
          settle({ kind: 'stopped', text: ACTION_EXPIRED_TEXT });
          return;
        case 'signed_out':
          settle({ kind: 'stopped', text: ACTION_SIGNED_OUT_TEXT });
          return;
        case 'aborted':
          // Nothing was decided, so the offer stands.
          setState({ kind: 'offered' });
          return;
        default:
          settle({ kind: 'stopped', text: ACTION_FAILED_TEXT });
      }
    });
  };

  if (state.kind === 'done') {
    return (
      <p className="sol-action-done" role="status">
        ✓ {state.text}
      </p>
    );
  }

  if (state.kind === 'stopped') {
    return (
      <p className="sol-action-stopped" role="alert">
        {state.text}
      </p>
    );
  }

  if (expired) {
    return (
      <p className="sol-action-stopped" role="status">
        {ACTION_EXPIRED_TEXT}
      </p>
    );
  }

  const busy = state.kind === 'confirming';

  return (
    <section
      className="sol-action"
      // Named by the change itself, so a screen reader hears WHAT is being
      // confirmed before it reaches the buttons.
      aria-label={`Confirm status change for ${captureLabel}`}
    >
      <p className="sol-action-prompt">{ACTION_CONFIRM_PROMPT}</p>
      <p className="sol-action-id">{captureLabel}</p>
      <p className="sol-action-location">
        {display.locationDisplay ?? 'Location not set'}
        {display.projectName ? ` · ${display.projectName}` : ''}
      </p>

      {/* **WORDS AND GLYPHS, NEVER COLOUR ALONE** — the same rule the status
          control follows, and it matters more here than anywhere: this is the
          line he is agreeing to. */}
      <p className="sol-action-transition">
        <span className={`sol-action-pill sol-action-pill--${display.fromStatus}`}>
          {fromLabel}
        </span>
        <span className="sol-action-arrow" aria-hidden="true">
          →
        </span>
        <span className={`sol-action-pill sol-action-pill--${display.toStatus}`}>{toLabel}</span>
      </p>

      <p className="sol-action-buttons">
        <button
          type="button"
          className="sol-action-confirm"
          disabled={busy || disabled === true}
          onClick={confirm}
        >
          {busy ? 'Confirming…' : 'Confirm'}
        </button>
        <button
          type="button"
          className="sol-action-cancel"
          disabled={busy}
          onClick={() => {
            // **NOTHING IS SENT.** No request, no ticket, no writer. The
            // proposal is simply never redeemed and expires on its own.
            settle({ kind: 'stopped', text: ACTION_CANCELLED_TEXT });
          }}
        >
          Cancel
        </button>
      </p>
    </section>
  );
}
